import { initCommerceApiWithGlobals } from 'common-api';
import { handleDLProductClickEvent } from 'common-ui';
import { selectCartDetails, useCartStore } from '../../../stores/cartStore';
import { useActionAddToCart } from './useActionAddToCart';
import type { ProductProps } from 'tsconfig/types';

type CustomCartItem = {
  itemCode: string;
  quantity: string | number;
  [key: string]: unknown;
};

const findProductByItemCode = (products: ProductProps[], itemCode: string) =>
  products?.find(productData => productData?.skus?.some(sku => sku.itemCode === itemCode));

export const useAddToCartCustomEvent = () => {
  const addToCart = useActionAddToCart();

  const trackAddToCart = async (items: CustomCartItem[], isInitialCart: boolean) => {
    try {
      const itemCodes = items.map(item => String(item.itemCode));
      const products = await initCommerceApiWithGlobals().Product.getAllProductData(itemCodes.join(','));
      const eventType = isInitialCart ? 'InitiateCart' : 'addToCart';

      items.forEach(item => {
        const product = findProductByItemCode(products, item.itemCode);

        if (product) {
          handleDLProductClickEvent(eventType, product, Number(item.quantity), undefined, item.itemCode);
        }
      });
    } catch {
      // Analytics must not change the result of a successful cart operation.
    }
  };

  const dispatchAddToCartStatus = (message: string, originalEvent: CustomEvent, error: boolean) => {
    document.dispatchEvent(
      new CustomEvent('aws-add-to-cart-status', {
        detail: { message, originalEvent, error },
      }),
    );
  };

  const addToCartCallback = async (event: CustomEvent<CustomCartItem[]>) => {
    try {
      if (!event?.detail) {
        throw new Error('Something went wrong. Please try again.');
      }
      const isInitialCart = selectCartDetails(useCartStore.getState()).itemsCount <= 0;
      await addToCart(event?.detail);
      trackAddToCart(event.detail, isInitialCart);
      // Dispatch a new event with the status of the add to cart action
      dispatchAddToCartStatus('Success', event, false);
    } catch (error: any) {
      // Dispatch a new event with the status of the add to cart action
      dispatchAddToCartStatus(error, event, true);
    }
  };

  return (event: Event): void => {
    addToCartCallback(event as CustomEvent<CustomCartItem[]>);
  };
};
